import React from 'react';
import { Box, Container, Typography, Grid, useTheme } from '@mui/material';
import Badge from '../ui/Badge';
import Button from '../ui/Button';
import Card from '../ui/Card';

export const HeroSection: React.FC = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  const handleScroll = (id: string) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <Box
      id="about"
      sx={{
        position: 'relative',
        minHeight: '85vh',
        display: 'flex',
        alignItems: 'center',
        overflow: 'hidden',
        borderBottom: `1px solid ${theme.palette.divider}`,
        backgroundColor: theme.palette.background.default,
        transition: 'background-color 0.3s ease, border-color 0.3s ease',
      }}
    >
      {/* Background Skyscraper Render */}
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          backgroundImage: `url('https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?q=80&w=1600&auto=format&fit=crop')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          filter: isDark ? 'brightness(0.35)' : 'brightness(0.85) contrast(0.95)',
          transition: 'filter 0.3s ease',
        }}
      />

      {/* Dynamic Theme Gradient Overlay */}
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          background: isDark
            ? 'linear-gradient(to right, #0b0f19 0%, rgba(11, 15, 25, 0.88) 60%, transparent 100%)'
            : 'linear-gradient(to right, #f7f9fb 0%, rgba(247, 249, 251, 0.92) 65%, rgba(247, 249, 251, 0.4) 100%)',
          transition: 'background 0.3s ease',
        }}
      />

      <Container maxWidth="xl" sx={{ position: 'relative', zIndex: 10, py: { xs: 8, md: 12 } }}>
        <Grid container spacing={6} alignItems="center">
          {/* Left Text Column */}
          <Grid item xs={12} lg={7}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              <Badge pulse label="Infrastructure Redefined" />

              <Typography
                variant="h1"
                sx={{
                  fontSize: { xs: '2.5rem', sm: '3.5rem', md: '4.25rem' },
                  lineHeight: 1.1,
                  color: theme.palette.text.primary,
                  fontFamily: "'BankGothic Md BT', 'BankGothic MD BT', 'Bank Gothic MD BT', 'Space Grotesk', sans-serif",
                  fontWeight: 700,
                }}
              >
                Complex Construction<br />
                <Box component="span" sx={{ color: theme.palette.primary.main }}>
                  Dependable Execution
                </Box>
              </Typography>

              <Typography
                variant="body1"
                sx={{
                  color: theme.palette.text.secondary,
                  fontSize: { xs: '1.05rem', md: '1.2rem' },
                  lineHeight: 1.6,
                  maxWidth: 600,
                }}
              >
                Inverika delivers specialised construction and infrastructure solutions across building envelopes, façade systems, glass, aluminium, civil works, and related infrastructure.
              </Typography>

              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, pt: 2 }}>
                <Button
                  variant="contained"
                  size="large"
                  glow
                  onClick={() => handleScroll('contact')}
                >
                  Discuss Your Project
                </Button>

                <Button
                  variant="outlined"
                  size="large"
                  onClick={() => handleScroll('capabilities')}
                  sx={{
                    backgroundColor: isDark ? 'rgba(22, 29, 47, 0.6)' : 'rgba(255, 255, 255, 0.8)',
                    backdropFilter: 'blur(12px)',
                    borderColor: isDark ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.2)',
                    color: theme.palette.text.primary,
                    '&:hover': {
                      backgroundColor: isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)',
                      borderColor: theme.palette.text.primary,
                    },
                  }}
                >
                  Explore Capabilities
                </Button>
              </Box>
            </Box>
          </Grid>

          {/* Right Trust Badges Strip (4 Glass Cards) */}
          <Grid item xs={12} lg={5}>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Card glass sx={{ p: 3, border: `1px solid ${theme.palette.divider}` }}>
                  <Typography
                    variant="h3"
                    sx={{ color: theme.palette.primary.main, fontFamily: 'Space Grotesk', fontWeight: 700, mb: 0.5 }}
                  >
                    10+
                  </Typography>
                  <Typography variant="caption" sx={{ color: theme.palette.text.secondary, textTransform: 'uppercase', letterSpacing: '0.08em', fontWeight: 600 }}>
                    Years Experience
                  </Typography>
                </Card>
              </Grid>

              <Grid item xs={6}>
                <Card glass sx={{ p: 3, border: `1px solid ${theme.palette.divider}` }}>
                  <Typography
                    variant="h3"
                    sx={{ color: theme.palette.primary.main, fontFamily: 'Space Grotesk', fontWeight: 700, mb: 0.5 }}
                  >
                    4
                  </Typography>
                  <Typography variant="caption" sx={{ color: theme.palette.text.secondary, textTransform: 'uppercase', letterSpacing: '0.08em', fontWeight: 600 }}>
                    Specialist Disciplines
                  </Typography>
                </Card>
              </Grid>

              <Grid item xs={6}>
                <Card glass sx={{ p: 3, border: `1px solid ${theme.palette.divider}` }}>
                  <span
                    className="material-symbols-outlined"
                    style={{ fontSize: 36, color: theme.palette.primary.main, display: 'block', marginBottom: 8 }}
                  >
                    verified_user
                  </span>
                  <Typography variant="caption" sx={{ color: theme.palette.text.secondary, textTransform: 'uppercase', letterSpacing: '0.08em', fontWeight: 600 }}>
                    Zero-Harm Safety
                  </Typography>
                </Card>
              </Grid>

              <Grid item xs={6}>
                <Card glass sx={{ p: 3, border: `1px solid ${theme.palette.divider}` }}>
                  <span
                    className="material-symbols-outlined"
                    style={{ fontSize: 36, color: theme.palette.primary.main, display: 'block', marginBottom: 8 }}
                  >
                    corporate_fare
                  </span>
                  <Typography variant="caption" sx={{ color: theme.palette.text.secondary, textTransform: 'uppercase', letterSpacing: '0.08em', fontWeight: 600 }}>
                    Multi-Sector Execution
                  </Typography>
                </Card>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default HeroSection;
